// I tre riquadri della scheda Excel dei clienti: Cliente, Impianto, Gestione (referenti).
import { useState, type ReactNode } from 'react'
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { CampoIndirizzo } from '../anagrafiche/CampoIndirizzo'
import { dataItaliana } from '../home/quadro'
import {
  ETICHETTA_RUOLO, RUOLI_REFERENTE, SchemaDatiCliente, SchemaDatiImpianto, SchemaReferente, TENSIONI,
  caricaScheda, eliminaReferente, salvaDatiCliente, salvaDatiImpianto, salvaReferente,
  type DatiCliente, type DatiImpiantoScheda, type DatiReferente,
} from './scheda.api'

type Modulo =
  | { tipo: 'cliente'; dati: DatiCliente }
  | { tipo: 'impianto'; dati: DatiImpiantoScheda }
  | { tipo: 'referente'; id: number | null; dati: DatiReferente }

const NUOVO_REFERENTE: DatiReferente = { ruolo: 'impianto', nome: '', email: '', email2: '', telefono: '' }
const TITOLO: Record<Modulo['tipo'], string> = { cliente: 'Dati del cliente', impianto: "Dati dell'impianto", referente: 'Referente' }

const indirizzo = (i: { indirizzo: string; comune: string; provincia: string }) =>
  [i.indirizzo, i.comune && `${i.comune}${i.provincia ? ` (${i.provincia})` : ''}`].filter(Boolean).join(', ') || '—'

function Riquadro(props: { id: string; titolo: string; azione: ReactNode; children: ReactNode }) {
  return (
    <section className="scheda-riquadro" aria-labelledby={props.id}>
      <header>
        <h2 id={props.id}>{props.titolo}</h2>
        {props.azione}
      </header>
      <dl>{props.children}</dl>
    </section>
  )
}

const Voce = ({ etichetta, children }: { etichetta: string; children: ReactNode }) => (
  <>
    <dt>{etichetta}</dt>
    <dd>{children || '—'}</dd>
  </>
)

export function SchedaImpianto({ impiantoId }: { impiantoId: number }) {
  const client = useQueryClient()
  const { data: s, error } = useQuery({ queryKey: ['scheda-impianto', impiantoId], queryFn: () => caricaScheda(impiantoId) })
  const [modulo, setModulo] = useState<Modulo | null>(null)
  const [errore, setErrore] = useState<string | null>(null)

  if (error) return <Alert severity="error">{error instanceof Error ? error.message : 'Scheda non disponibile'}</Alert>
  if (!s) return null

  const apri = (m: Modulo) => {
    setErrore(null)
    setModulo(m)
  }
  const modifica = (etichetta: string, m: Modulo) => (
    <Button size="small" aria-label={`Modifica ${etichetta}`} onClick={() => apri(m)}>
      Modifica
    </Button>
  )

  async function esegui(azione: () => Promise<void>) {
    setErrore(null)
    try {
      await azione()
      setModulo(null)
      await client.invalidateQueries({ queryKey: ['scheda-impianto', impiantoId] })
    } catch (e) {
      setErrore(e instanceof Error ? e.message : 'errore imprevisto')
    }
  }

  function suSalva() {
    const m = modulo
    if (m === null) return
    // Ogni ramo valida col suo schema prima di scrivere: l'errore resta nel dialogo.
    const prova = <T,>(schema: { safeParse: (v: unknown) => { success: boolean; error?: { issues: { message: string }[] } } }, scrivi: () => Promise<T>) => {
      const esito = schema.safeParse(m.dati)
      if (!esito.success) return setErrore(esito.error!.issues[0]!.message)
      void esegui(async () => void (await scrivi()))
    }
    if (m.tipo === 'cliente') prova(SchemaDatiCliente, () => salvaDatiCliente(s!.aziendaId, m.dati))
    else if (m.tipo === 'impianto') prova(SchemaDatiImpianto, () => salvaDatiImpianto(impiantoId, m.dati))
    else prova(SchemaReferente, () => salvaReferente(m.id, impiantoId, m.dati))
  }

  const campo = <M extends Modulo>(m: M, chiave: keyof M['dati'] & string, valore: unknown) =>
    setModulo({ ...m, dati: { ...m.dati, [chiave]: valore } } as Modulo)

  return (
    <div className="scheda">
      <Riquadro
        id="riquadro-cliente"
        titolo="Cliente"
        azione={modifica('dati del cliente', { tipo: 'cliente', dati: { sedeLegale: s.sedeLegale, clienteDal: s.clienteDal ?? '' } })}
      >
        <Voce etichetta="Ragione sociale"><strong>{s.ragioneSociale}</strong></Voce>
        <Voce etichetta="P.IVA / C.F.">{s.partitaIva ?? s.codiceFiscale}</Voce>
        <Voce etichetta="Sede legale">{indirizzo(s.sedeLegale)}</Voce>
        <Voce etichetta="Cliente dal">{s.clienteDal && dataItaliana(s.clienteDal)}</Voce>
      </Riquadro>

      <Riquadro
        id="riquadro-impianto"
        titolo="Impianto"
        azione={modifica("dati dell'impianto", {
          tipo: 'impianto',
          dati: { censimp: s.censimp ?? '', dataAttivazione: s.dataAttivazione ?? '', tensione: s.tensione, contrattiGse: s.contrattiGse.join(', ') },
        })}
      >
        <Voce etichetta="Potenza">{s.potenza && <strong>{`${s.potenza} kW${s.tensione ? ` · ${s.tensione}` : ''}`}</strong>}</Voce>
        <Voce etichetta="Licenza ADM">{[s.codiceDitta, s.ufficio].filter(Boolean).join(' · ')}</Voce>
        <Voce etichetta="POD">{s.pod.join(', ')}</Voce>
        <Voce etichetta="CENSIMP">{s.censimp}</Voce>
        <Voce etichetta="Attivazione">{s.dataAttivazione && dataItaliana(s.dataAttivazione)}</Voce>
        <Voce etichetta="Contratti GSE">{s.contrattiGse.join(', ')}</Voce>
        <Voce etichetta="Indirizzo">{indirizzo(s.ubicazione)}</Voce>
      </Riquadro>

      <Riquadro
        id="riquadro-gestione"
        titolo="Gestione"
        azione={
          <Button size="small" onClick={() => apri({ tipo: 'referente', id: null, dati: NUOVO_REFERENTE })}>
            Aggiungi referente
          </Button>
        }
      >
        {s.referenti.length === 0 && <Voce etichetta="Referenti">Nessun referente</Voce>}
        {s.referenti.map((r) => (
          <Voce key={r.id} etichetta={ETICHETTA_RUOLO[r.ruolo]}>
            <strong>{r.nome}</strong>
            <span className="scheda-contatti">
              {[r.email, r.email2, r.telefono].filter(Boolean).map((c) =>
                c!.includes('@') ? <a key={c} href={`mailto:${c}`}>{c}</a> : <a key={c} href={`tel:${c!.replace(/\s/g, '')}`}>{c}</a>,
              )}
            </span>
            <span className="scheda-azioni">
              {modifica(r.nome, {
                tipo: 'referente',
                id: r.id,
                dati: { ruolo: r.ruolo, nome: r.nome, email: r.email ?? '', email2: r.email2 ?? '', telefono: r.telefono ?? '' },
              })}
              <Button
                size="small"
                color="error"
                aria-label={`Elimina ${r.nome}`}
                onClick={() => {
                  if (window.confirm(`Eliminare il referente «${r.nome}»?`)) void esegui(() => eliminaReferente(r.id))
                }}
              >
                Elimina
              </Button>
            </span>
          </Voce>
        ))}
      </Riquadro>

      <Dialog open={modulo !== null} onClose={() => setModulo(null)} fullWidth maxWidth="sm">
        <DialogTitle>{modulo && TITOLO[modulo.tipo]}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {errore !== null && <Alert severity="error" role="alert">{errore}</Alert>}
            {modulo?.tipo === 'cliente' && (
              <>
                <CampoIndirizzo etichetta="Sede legale" valore={modulo.dati.sedeLegale} onChange={(v) => campo(modulo, 'sedeLegale', v)} />
                <TextField size="small" type="date" label="Cliente dal" slotProps={{ inputLabel: { shrink: true } }}
                  value={modulo.dati.clienteDal} onChange={(e) => campo(modulo, 'clienteDal', e.target.value)} />
              </>
            )}
            {modulo?.tipo === 'impianto' && (
              <>
                <TextField size="small" label="CENSIMP" value={modulo.dati.censimp} onChange={(e) => campo(modulo, 'censimp', e.target.value)} />
                <TextField size="small" type="date" label="Data di attivazione" slotProps={{ inputLabel: { shrink: true } }}
                  value={modulo.dati.dataAttivazione} onChange={(e) => campo(modulo, 'dataAttivazione', e.target.value)} />
                <TextField select size="small" label="Tensione" slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
                  value={modulo.dati.tensione ?? ''} onChange={(e) => campo(modulo, 'tensione', e.target.value || null)}>
                  <option value="">Non indicata</option>
                  {TENSIONI.map((t) => <option key={t} value={t}>{t}</option>)}
                </TextField>
                <TextField size="small" label="Contratti GSE" helperText="Separati da virgola" multiline
                  value={modulo.dati.contrattiGse} onChange={(e) => campo(modulo, 'contrattiGse', e.target.value)} />
              </>
            )}
            {modulo?.tipo === 'referente' && (
              <>
                <TextField select size="small" label="Ruolo" slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
                  value={modulo.dati.ruolo} onChange={(e) => campo(modulo, 'ruolo', e.target.value)}>
                  {RUOLI_REFERENTE.map((r) => <option key={r} value={r}>{ETICHETTA_RUOLO[r]}</option>)}
                </TextField>
                <TextField size="small" label="Nome" value={modulo.dati.nome} onChange={(e) => campo(modulo, 'nome', e.target.value)} />
                <TextField size="small" type="email" label="Email" value={modulo.dati.email} onChange={(e) => campo(modulo, 'email', e.target.value)} />
                <TextField size="small" type="email" label="Seconda email" value={modulo.dati.email2} onChange={(e) => campo(modulo, 'email2', e.target.value)} />
                <TextField size="small" type="tel" label="Telefono" value={modulo.dati.telefono} onChange={(e) => campo(modulo, 'telefono', e.target.value)} />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModulo(null)}>Annulla</Button>
          <Button variant="contained" onClick={suSalva}>Salva</Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

